import axios, { AxiosHeaders, isAxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import {
  ApiError,
  CODIGO_DPOP_INDISPONIVEL,
  TEXTO_DPOP_INDISPONIVEL,
  ehRedeBloqueada,
  parseApiError,
} from './errors'
import { TEXTO_REDE_BLOQUEADA, bloquear } from '@/lib/auth/redeBloqueada'
import { compararAcesso, lerAcesso, lerEsquema, type EsquemaDoAcesso } from '@/lib/auth/acesso'
import { guardarNonceDa, pedeNovoNonce, provarPedido } from './dpopDoPedido'
import { renovar } from '@/lib/auth/renovacao'
import { esperarFimDaOperacao, estaAbaEstaSaindo } from '@/lib/auth/sessaoEntreAbas'
import { cabecalhosDeclarados } from '@/lib/declaradoDoCliente'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Interno (faixa 25 §3.5, I4): este pedido JÁ é a repetição depois de uma renovação. 401 de novo = sessão encerrada. */
    _repetidoAposRenovacao?: boolean
    /** Interno (faixa 33 §4, B5): este pedido JÁ é a repetição por `use_dpop_nonce`. Recusa de novo = erro comum. */
    _repetidoPorNonce?: boolean
    /** Interno (D4): `Date.now()` do 1º interceptor — a repetição por nonce leva só o que sobrou do `timeout`. */
    _inicioDoPedido?: number
  }
}

/**
 * Rotas cujo 401 quem chama trata (faixa 25 §3.5): login e troca de senha (401 é credencial — senha errada,
 * I6), refresh (o 401 É o veredito), logout e logout-all (renovam e repetem sozinhos, dentro do trinco —
 * `lib/auth/saidaNoServidor`). `/auth/logout` já casa `/auth/logout-all` pelo `includes`.
 */
const ROTAS_QUE_TRATAM_O_PROPRIO_401 = [
  '/auth/login',
  '/auth/refresh',
  '/auth/change-password',
  '/auth/logout',
  '/auth/logout-all',
]

/** Sem `Authorization` (C3): o refresh prova a sessão pelo cookie; o login ainda não tem sessão. */
const ROTAS_SEM_AUTHORIZATION = ['/auth/login', '/auth/refresh']

/** A troca de senha prova a chave em QUALQUER esquema: a sessão nova nasce presa mesmo vindo de bearer (faixa 33 §5). */
const ROTA_TROCA_DE_SENHA = ['/auth/change-password']

/** A única rota cuja chave falhando fecha a porta com mensagem própria (D1); nas outras é falha de rede. */
const ROTA_DE_LOGIN = ['/auth/login']

type ModuloSaida = typeof import('@/lib/auth/saida')

/**
 * `@/lib/auth/saida` entra por `import()` DINÂMICO, nunca estático: ele importa `@/lib/cache` (barrel), que
 * reexporta `useCachedQuery`, que importa `@/providers/AuthContext`, que importa `api` DESTE arquivo — ciclo
 * `client → saida → cache → useCachedQuery → AuthContext → client`. Carregado aqui, só na hora da saída.
 */
function carregarSaida(): Promise<ModuloSaida> {
  return import('@/lib/auth/saida')
}

function casa(url: string | undefined, rotas: readonly string[]): boolean {
  const alvo = url ?? ''
  return rotas.some((rota) => alvo.includes(rota))
}

function trataOProprio401(config: InternalAxiosRequestConfig): boolean {
  return casa(config.url, ROTAS_QUE_TRATAM_O_PROPRIO_401)
}

interface CredencialDoPedido {
  esquema: EsquemaDoAcesso
  token: string
}

/** Esquema e access que o pedido levou no `Authorization` (posto pelo interceptor de request no fim do arquivo). */
function credencialDoPedido(config: InternalAxiosRequestConfig | undefined): CredencialDoPedido | null {
  const headers = config?.headers
  if (!headers) return null
  // `AxiosHeaders.get` acha a chave sem diferenciar caixa; objeto cru cai no acesso direto.
  const bruto: unknown =
    typeof headers.get === 'function'
      ? headers.get('Authorization')
      : (headers as Record<string, unknown>).Authorization
  if (typeof bruto !== 'string') return null
  for (const esquema of ['Bearer', 'DPoP'] as const) {
    if (bruto.startsWith(`${esquema} `)) return { esquema, token: bruto.slice(esquema.length + 1) }
  }
  return null
}

function tokenDoPedido(config: InternalAxiosRequestConfig | undefined): string | null {
  return credencialDoPedido(config)?.token ?? null
}


/**
 * Repete na hora (o interceptor de request faz prova NOVA com o nonce novo), com o MESMO `Authorization`, UMA vez
 * (B5). Leva só o que sobrou do `timeout` (D4): a repetição não dobra o teto de quem segura o trinco entre abas.
 */
function repetirComNonce(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const headers = AxiosHeaders.from(config.headers)
  headers.delete('DPoP')
  const decorrido = Date.now() - (config._inicioDoPedido ?? Date.now())
  const timeout = config.timeout ? Math.max(1, config.timeout - decorrido) : config.timeout
  return api.request({ ...config, headers, timeout, _repetidoPorNonce: true })
}

function erroDeRede(): ApiError {
  return new ApiError(0, 'NETWORK', 'Falha de conexão com o servidor')
}

function erroDeDpopIndisponivel(): ApiError {
  return new ApiError(0, CODIGO_DPOP_INDISPONIVEL, TEXTO_DPOP_INDISPONIVEL, undefined, undefined, undefined, true)
}

/** Sintetizado de propósito como sessão perdida: `codigoExplicito` true. */
function erroDeHtml(): ApiError {
  return new ApiError(401, 'UNAUTHENTICATED', 'Sessão encerrada — entre de novo para continuar', undefined, undefined, undefined, true)
}

/** O 403 de rede de um pedido cuja RENOVAÇÃO foi recusada pela rede: o mesmo erro de um 403 direto. */
function erroDeRedeBloqueada(): ApiError {
  return new ApiError(403, 'NETWORK_FORBIDDEN', TEXTO_REDE_BLOQUEADA, undefined, undefined, undefined, true)
}

/**
 * Barra HTML chegando onde se esperava JSON. Até a faixa 25 a causa era o 307 do `middleware.ts` pro `/login`
 * com a API na mesma origem (`NEXT_PUBLIC_API_URL` ausente): o navegador seguia o redirect e o axios entregava
 * 200 com a página de login no `data`. O 307 sumiu, mas a regra fica (spec §8): HTML numa rota comum é tratado
 * como 401 comum (renova e repete); numa rota que trata o próprio 401, só rejeita. Blob passa direto —
 * `services/media` e `services/profile` pedem `responseType: 'blob'`.
 */
export function onResponse(response: AxiosResponse): Promise<AxiosResponse> {
  guardarNonceDa(response.headers)
  const responseType = response.config?.responseType
  const binario = responseType === 'blob' || responseType === 'arraybuffer'
  const contentType = String(response.headers?.['content-type'] ?? '')
  if (!binario && contentType.includes('text/html')) {
    const config = response.config
    if (typeof window === 'undefined' || !config || trataOProprio401(config)) return Promise.reject(erroDeHtml())
    return aposNaoAutenticado(config, erroDeHtml())
  }
  /* 2xx cujo corpo não virou JSON: com `silentJSONParsing` o axios entrega a STRING
     crua de um corpo vazio ou truncado (queda de rede medida em prod, 2026-10-21), e
     `x?.campo.sub` lança em render — `?.` cobre nullish, não string. Nenhuma rota que
     o front chama devolve string JSON pura; 204 é o único 2xx sem corpo legítimo.
     Vira falha de REDE (sem desautenticar): quem consome mantém o último dado bom. */
  if (!binario && response.status !== 204 && typeof response.data === 'string') {
    return Promise.reject(new ApiError(0, 'NETWORK', 'Resposta incompleta do servidor'))
  }
  return Promise.resolve(response)
}

/**
 * `Retry-After` do HEADER entra quando o corpo não trouxe `retry_after_seconds`
 * (o corpo vence). Nasceu do 429 `AUTH_LOCKED` do login/troca de senha, que só
 * manda o tempo no header (back `exceptions/handlers.py::_auth_bloqueado`, exposto
 * pelo CORS em `app/main.py`). Só inteiro positivo em segundos: HTTP-date ou lixo
 * fica `undefined` em vez de inventar espera. `AxiosHeaders` normaliza pra minúsculas.
 */
function comRetryAfter(apiError: ApiError, headers: unknown): ApiError {
  if (apiError.retryAfterSeconds !== undefined) return apiError
  if (typeof headers !== 'object' || headers === null) return apiError
  const raw = (headers as Record<string, unknown>)['retry-after']
  if (typeof raw !== 'string' && typeof raw !== 'number') return apiError
  if (typeof raw === 'string' && raw.trim() === '') return apiError
  const segundos = Number(raw)
  if (!Number.isFinite(segundos) || segundos <= 0) return apiError
  return new ApiError(
    apiError.status,
    apiError.code,
    apiError.detail,
    Math.floor(segundos),
    apiError.dispatchId,
    apiError.meta,
    apiError.codigoExplicito,
    { chave: apiError.chave, ip: apiError.ip },
  )
}

/**
 * Converte o erro do axios em `ApiError` e, no 401 de rota comum, renova e repete (faixa 25 §3.5). Pode
 * RESOLVER com a resposta da repetição. `ApiError` pronto passa intacto: é a rejeição do PRÓPRIO interceptor de
 * request (`DPOP_INDISPONIVEL`, rede local), que no axios desce a cadeia até aqui (`lib/core/Axios.js`, cadeia
 * assíncrona) — sem isso viraria `NETWORK`.
 */
export async function onResponseError(error: unknown): Promise<AxiosResponse> {
  if (error instanceof ApiError) throw error
  if (!isAxiosError(error) || !error.response) throw erroDeRede()
  guardarNonceDa(error.response.headers)
  const apiError = comRetryAfter(parseApiError(error.response.status, error.response.data), error.response.headers)
  const config = error.config
  // Antes do bloqueio de rede, do `trataOProprio401` e da renovação: o 401 de nonce NÃO é sessão vencida.
  if (config && pedeNovoNonce(config, error.response.status, apiError.code, error.response.headers)) return repetirComNonce(config)
  // Faixa 28 §4: IP fora da rede permitida, em QUALQUER rota — acende o bloqueio (a tela de bloqueio cobre o app) e
  // rejeita com o próprio erro; a tela de trás não muda de estado sozinha. Nada renova, nada sai: o cookie vale.
  if (ehRedeBloqueada(apiError) && typeof window !== 'undefined') bloquear()
  if (apiError.status === 401 && typeof window !== 'undefined' && config && !trataOProprio401(config)) {
    return aposNaoAutenticado(config, apiError)
  }
  throw apiError
}

/**
 * §3.5: UMA renovação e UMA repetição (I4). N pedidos com 401 juntos esperam a MESMA renovação (single-flight
 * em `renovar`). A repetição que leva 401 de novo é sessão morta — com a guarda de I7 (`compararAcesso`) — e sai
 * por `sairPorSessaoMorta`: se outra aba gravou motivo ("Sair" dela, cujo `storage` acorda a espera antes da
 * mensagem do canal, ou sem canal nenhum), segue esse motivo; sem motivo, "sessão encerrada" (I8).
 * Renovação indisponível: o pedido falha como rede e nada local muda (I5). Sessão morta: a saída da aba aberta.
 */
async function aposNaoAutenticado(config: InternalAxiosRequestConfig, erro: ApiError): Promise<AxiosResponse> {
  // Operação anunciada ("Sair", troca de senha) decide antes deste 401: o "Sair" anuncia ANTES do 204, e a marca
  // de `estaAbaEstaSaindo` só nasce no fim dele — sem esperar, uma repetição atrasada daria 'igual' e gravaria
  // 'encerrada' por cima do 'saiu' (I8). `renovar` já espera por dentro; a repetição não passa por ele.
  await esperarFimDaOperacao()
  // Carregado ANTES das guardas: entre a guarda e a saída não pode haver `await` (dois 401 sairiam duas vezes).
  const saida = await carregarSaida()
  if (estaAbaEstaSaindo()) throw erro
  const usado = tokenDoPedido(config)
  if (config._repetidoAposRenovacao) {
    if (compararAcesso(usado) === 'igual') saida.sairPorSessaoMorta()
    throw erro
  }
  const resultado = await renovar({ acessoDoPedido: usado })
  if (resultado.tipo === 'renovada') {
    // O Authorization velho sai; o interceptor de request põe o da memória (e prova nova). O carimbo zera, mas o
    // `timeout` vem do `config`: se o pedido já repetiu por nonce, herda o que sobrou, não o teto cheio (D4). Os
    // DELETE de Meus acessos (sessão/dispositivo, `TIMEOUT_DESCONEXAO_MS`) chegam aqui, e a repetição recebe o teto
    // cheio: no pior caso, pedido + renovação + repetição. As demais rotas com teto estão em ROTAS_QUE_TRATAM_O_PROPRIO_401.
    const headers = AxiosHeaders.from(config.headers)
    headers.delete('Authorization')
    return api.request({ ...config, headers, _repetidoAposRenovacao: true, _inicioDoPedido: undefined })
  }
  if (resultado.tipo === 'indisponivel') throw erroDeRede()
  // Renovação recusada pela rede: o bloqueio já acendeu na renovação. Falha com o MESMO erro de um 403 de rede direto.
  if (resultado.tipo === 'rede-bloqueada') throw erroDeRedeBloqueada()
  saida.sairPorSessaoMorta()
  throw erro
}


export const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  /**
   * Array vira param REPETIDO (`?kind=a&kind=b`), que é a única forma que o
   * FastAPI lê como lista. O default do axios emite `kind[]=a` — e param
   * desconhecido o back DESCARTA em silêncio, sem 422 (handoff arco A §5.2):
   * o filtro não aplica, a resposta vem completa e nada acusa na tela. Fica na
   * criação do client, não no call site, porque o modo de falha é invisível e
   * um único serviço esquecido bastaria pra reintroduzi-lo.
   */
  paramsSerializer: { indexes: null },
})


/** Prova DPoP do pedido. Chave `falhou`: o pedido NÃO sai — no login, `DPOP_INDISPONIVEL` (D1); no resto, rede (B9). */
async function comProva(config: InternalAxiosRequestConfig, accessToken?: string): Promise<InternalAxiosRequestConfig> {
  if ((await provarPedido(config, accessToken)) === 'falhou') {
    throw casa(config.url, ROTA_DE_LOGIN) ? erroDeDpopIndisponivel() : erroDeRede()
  }
  return config
}

api.interceptors.request.use(async (config) => {
  // Prova é de uso único (B2): a de uma tentativa anterior nunca vai de novo, e um rollback pra Bearer não leva
  // prova com `ath` de outro token (header `DPoP` presente é sempre conferido no back).
  config.headers.delete('DPoP')
  config._inicioDoPedido ??= Date.now()
  // Faixa 39 §1: toda request (login/refresh inclusive) declara build, fuso e a tela de AGORA.
  for (const [nome, valor] of Object.entries(cabecalhosDeclarados())) config.headers.set(nome, valor)
  if (casa(config.url, ROTAS_SEM_AUTHORIZATION)) {
    config.headers.delete('Authorization')
    return comProva(config)
  }
  // Authorization explícito vence a memória: é o access renovado DENTRO da saída (`services/auth`
  // `logout({ acesso })`), que de propósito não entra na memória de quem está saindo.
  if (!config.headers.has('Authorization')) {
    const acesso = lerAcesso()
    if (acesso) config.headers.set('Authorization', `${lerEsquema()} ${acesso}`)
  }
  const credencial = credencialDoPedido(config)
  if (credencial && (credencial.esquema === 'DPoP' || casa(config.url, ROTA_TROCA_DE_SENHA))) {
    return comProva(config, credencial.token)
  }
  // Bearer em rota comum: sem prova — nada a ganhar, e header presente é sempre conferido.
  return config
})

api.interceptors.response.use(onResponse, onResponseError)
